/* eslint-disable @atlaskit/platform/one-value-export-per-file -- meta key + overlap helpers share one dismiss path */
import { AGENT_REMOTE_EDIT_REVIEW_DATA } from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import type { ReadonlyTransaction, Transaction } from '@atlaskit/editor-prosemirror/state';
import { AddMarkStep, RemoveMarkStep, type Mapping } from '@atlaskit/editor-prosemirror/transform';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';

import { isReviewMomentEnabledNoExposure } from '../../ui/utils/review-moment-enabled';
import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import { AI_CONTENT_SEGMENT_DATA, AI_FINAL_STREAMING_TRANSFORMATION_DATA } from './meta-keys';
import type { AIContentPositionEntry, SessionContextPluginState } from './types';

/**
 * Meta on Review moment Undo/Redo / re-anchor transactions so a
 * slice-replace that overlaps stored AI ranges does not look like a user edit
 * and tear the review down.
 */
export const IS_REVIEW_MOMENT_TRANSFORM = 'isReviewMomentTransform';

const collectEntryRanges = (
	entries: AIContentPositionEntry[] | undefined,
	ranges: Array<{ endPos: number; startPos: number }>,
): void => {
	entries?.forEach((entry) => {
		if (entry.diffSegments?.length) {
			collectEntryRanges(entry.diffSegments, ranges);
			return;
		}
		ranges.push({ startPos: entry.startPos, endPos: entry.endPos });
	});
};

/**
 * Ranges Undo/Redo would replace: fine step-through segments when present,
 * otherwise the coarse `aiContentPositions` working set.
 */
export const collectReviewRanges = (
	pluginState: SessionContextPluginState,
): Array<{ endPos: number; startPos: number }> => {
	const ranges: Array<{ endPos: number; startPos: number }> = [];
	if (pluginState.streamReviewSegments?.length) {
		collectEntryRanges(pluginState.streamReviewSegments, ranges);
	}
	if (!ranges.length) {
		collectEntryRanges(pluginState.aiContentPositions, ranges);
	}
	return ranges;
};

export const mappingOverlapsRanges = (
	mapping: Mapping,
	ranges: ReadonlyArray<{ endPos: number; startPos: number }>,
	steps: readonly Step[] = [],
): boolean =>
	ranges.some((range) => mappingOverlapsRange(mapping, range.startPos, range.endPos, steps));

const rangesOverlap = (oldStart: number, oldEnd: number, from: number, to: number): boolean => {
	if (oldStart === oldEnd) {
		return oldStart >= from && oldStart < to;
	}
	return oldStart < to && oldEnd > from;
};

// AddMarkStep / RemoveMarkStep change the doc but emit an empty StepMap, so
// `map.forEach` never visits their span. The affected range is on the step.
const getPositionNeutralStepRange = (step: Step): { from: number; to: number } | null => {
	if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
		return { from: step.from, to: step.to };
	}
	return null;
};

const mappingOverlapsRange = (
	mapping: Mapping,
	startPos: number,
	endPos: number,
	steps: readonly Step[],
): boolean => {
	let from = Math.min(startPos, endPos);
	let to = Math.max(startPos, endPos);
	if (from === to) {
		return false;
	}

	for (let i = 0; i < mapping.maps.length; i++) {
		const map = mapping.maps[i];
		let overlapped = false;
		map.forEach((oldStart, oldEnd, newStart, newEnd) => {
			// A map with both spans empty, such as an AnalyticsStep, is ignored.
			if (oldStart === oldEnd && newStart === newEnd) {
				return;
			}
			if (rangesOverlap(oldStart, oldEnd, from, to)) {
				overlapped = true;
			}
		});
		if (!overlapped) {
			const markRange = steps[i] ? getPositionNeutralStepRange(steps[i]) : null;
			if (markRange && rangesOverlap(markRange.from, markRange.to, from, to)) {
				overlapped = true;
			}
		}
		if (overlapped) {
			return true;
		}
		from = map.map(from, 1);
		to = map.map(to, -1);
		if (to < from) {
			const swapped = from;
			from = to;
			to = swapped;
		}
	}

	return false;
};

const isReviewOwnedTransaction = (tr: Transaction | ReadonlyTransaction): boolean =>
	!!(
		tr.getMeta(IS_REVIEW_MOMENT_TRANSFORM) ||
		tr.getMeta('isAIStreamingTransformation') ||
		tr.getMeta('isAIRestoreTransaction') ||
		tr.getMeta(AI_FINAL_STREAMING_TRANSFORMATION_DATA) ||
		tr.getMeta(AI_CONTENT_SEGMENT_DATA) ||
		tr.getMeta(AGENT_REMOTE_EDIT_REVIEW_DATA)
	);

/**
 * True when a doc-changing transaction is a user (or collab) edit overlapping
 * a live Review moment range. Callers RESET the session so View changes /
 * Undo / Redo cannot overwrite that edit.
 *
 * Uses pre-remap `pluginState` ranges: `tr.mapping` is relative to the old doc.
 */
export const shouldDismissReviewOnUserEdit = (
	tr: Transaction | ReadonlyTransaction,
	pluginState: SessionContextPluginState,
): boolean => {
	if (!isReviewMomentEnabledNoExposure()) {
		return false;
	}
	if (pluginState.status !== 'postStreamReview' || !tr.docChanged) {
		return false;
	}
	// Session-context actions and AI-owned writes overlap stored ranges
	// but are not a user dismiss.
	if (tr.getMeta(aiSessionContextPluginKey) || isReviewOwnedTransaction(tr)) {
		return false;
	}

	return mappingOverlapsRanges(tr.mapping, collectReviewRanges(pluginState), tr.steps);
};
